import React, { useEffect, useReducer, useState, ReactNode } from "react";
import { mainReducer } from "./reducer";
import sendRequest, { tokenRef } from "src/helpers/service";
import { readStoredUser } from "src/helpers/storedSession";
import { UserState, UserContextType, User, UserAccessPage } from "./types";

/**
 * The session as the last login left it.
 *
 * Read lazily, when the provider mounts, rather than at module load - a module
 * side effect would fire before a test could arrange localStorage, and before
 * anything is in a position to react to it.
 */
const getDefaultState = (): UserState => {
  let user: string | User | null = localStorage.getItem("user");
  const pages = localStorage.getItem("p");
  try {
    if (user && pages) {
      try {
        user = JSON.parse(decodeURIComponent(window.atob(localStorage.getItem("user") || "")));
      } catch (error) {
        localStorage.removeItem("user");
      }
      if (user && typeof user === 'object' && 'token' in user) {
        // @ts-ignore - tokenRef.current is mutable
        tokenRef.current = user.token;

        return {
          user: user as User,
          userAccessPages: JSON.parse(decodeURIComponent(window.atob(localStorage.getItem("p") || ""))),
        };
      }
    }
    localStorage.removeItem("token");
    localStorage.removeItem("p");
    return { user: false, userAccessPages: [] };
  } catch (error) {
    return { user: false, userAccessPages: [] };
  }
};

interface SessionResponse {
  user: User;
  pages: any[];
}

export const UserContext = React.createContext<UserContextType>({
  user: false,
  userAccessPages: [],
  contactUsCount: 0,
  sessionReady: false,
  logout: () => {},
  setUser: () => {},
  setContactUsCount: () => {},
  setUserAccessPages: () => {},
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(mainReducer, undefined as any, getDefaultState);
  // Nothing is cached on the login page, so there is nothing to refresh and
  // nothing to wait for.
  const [sessionReady, setSessionReady] = useState(!(state as UserState).user);

  const setUser = (payload: User | false) => {
    dispatch({ type: "SET_USER", payload });
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("p");
    dispatch({ type: "LOGOUT" });
    (window as any).navigate("/login");
  };

  const setUserAccessPages = (payload: any) => {
    if (!payload) return;

    const mappedPages: UserAccessPage[] = payload.map((page: any) => ({
      name: page.name,
      write: page.write_permission,
      read: page.read_permission,
      delete: page.delete_permission,
      page_id: page.page_id,
    }));

    localStorage.setItem("p", window.btoa(encodeURIComponent(JSON.stringify(mappedPages))));

    dispatch({ type: "SET_USER_ACCESS_PAGES", payload: mappedPages });
  };

  /**
   * Pick up a permission change on a reload rather than on a re-login.
   *
   * Abilities are baked into the Sanctum token when it is created, so a role
   * that gains a page leaves an admin with a live session holding a token that
   * cannot reach it. `auth/session` hands back both the current pages and a
   * token carrying the current abilities.
   */
  useEffect(() => {
    if (!(state as UserState).user) return;

    let cancelled = false;

    sendRequest
      .get<SessionResponse>({ url: "auth/session" })
      .then((res) => {
        if (cancelled || !res?.data?.user) return;

        localStorage.setItem(
          "user",
          window.btoa(encodeURIComponent(JSON.stringify(res.data.user)))
        );
        // @ts-ignore - tokenRef.current is mutable
        tokenRef.current = res.data.user.token;
        setUser(res.data.user);
        setUserAccessPages(res.data.pages);
      })
      .catch(() => {
        // Deliberately silent: a network blip or a 500 must not lock an admin
        // out of the panel, so the session cached at login stands. A 401 is
        // the one refusal that signs them out, and sendRequest does that.
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []); //eslint-disable-line

  /**
   * Keep this tab on the same session as the browser's other tabs.
   *
   * They share `localStorage` but each holds its own `tokenRef`. When one tab
   * is handed a renewed token, the others would keep sending the old one until
   * it stopped working and then sign out - clearing the shared session, so the
   * next reload anywhere landed on the login page. A `storage` event fires in
   * every tab except the one that wrote, which is exactly the set to update.
   */
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.storageArea !== localStorage) return;

      if (event.key === "user" || event.key === null) {
        const stored = readStoredUser();
        if (stored) {
          // @ts-ignore - tokenRef.current is mutable
          tokenRef.current = stored.token;
          setUser(stored);
        } else if ((state as UserState).user) {
          // Signed out in another tab. The storage is already clear.
          dispatch({ type: "LOGOUT" });
          (window as any).navigate?.("/login");
        }
      }

      if (event.key === "p" && event.newValue) {
        try {
          dispatch({
            type: "SET_USER_ACCESS_PAGES",
            payload: JSON.parse(decodeURIComponent(window.atob(event.newValue))),
          });
        } catch (error) {
          // A malformed write from elsewhere leaves this tab's pages as they are.
        }
      }
    }

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [state]); //eslint-disable-line

  const setContactUsCount = (payload: number) => {
    dispatch({ type: "SET_COUNTACT_US_COUNT", payload });
  };

  const userState = state as UserState;

  const value: UserContextType = {
    user: userState.user,
    contactUsCount: userState.contactUsCount,
    userAccessPages: userState.userAccessPages,
    sessionReady,
    logout,
    setUser,
    setContactUsCount,
    setUserAccessPages,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
